/** @format */

import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import Papa from "papaparse";
import { useState } from "react";

type CsvImportProps = {
	onFileReady?: (file: File | null) => void;
};
type ProgressStatus = "idle" | "uploading" | "success" | "error";

export const CsvImport = ({ onFileReady }: CsvImportProps) => {
	const { t } = useTranslation();

	const [file, setFile] = useState<File | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [status, setStatus] = useState<ProgressStatus>("idle");

	const { getRootProps, getInputProps } = useDropzone({
		accept: {
			"text/csv": [".csv"],
		},
		maxSize: 5 * 1024 * 1024,
		maxFiles: 1,
		onDrop: (acceptedFiles) => {
			setError(null);
			if (acceptedFiles.length === 0) {
				return;
			}
			setStatus("uploading");
			const myFile = acceptedFiles[0];
			Papa.parse(myFile, {
				header: true,
				complete: (_results) => {
					setFile(myFile);
					setStatus("success");
					setError(null);
					onFileReady?.(myFile);
				},
				error: (error) => {
					setStatus("error");
					setError(`${t("csvImport.errorParseCSV")}: ${error.message}`);
				},
			});
		},
		onDropRejected: (rejectedFiles) => {
			setStatus("error");
			const errorCode = rejectedFiles[0].errors[0].code;
			if (errorCode === "file-too-large") {
				setError(t("csvImport.fileTooLarge"));
			} else if (errorCode === "file-invalid-type") {
				setError(t("csvImport.fileInvalid"));
			} else if (errorCode === "too-many-files") {
				setError("Only one file can be uploaded at a time. Please upload one CSV file.");
			}
		},
	});

	const handleRemove = () => {
		setFile(null);
		setStatus("idle");
		setError(null);
		onFileReady?.(null);
	};

	const uploadContent =
		file && status === "success" ? (
			<div className="flex flex-row gap-3 items-center justify-center border border-slate-200 rounded-lg px-3 py-2 mb-2 shadow-(--admin-shadow-raised)">
				<p className="text-(--admin-color-primary-strong) font-semibold">{file.name}</p>
				<button
					aria-label={t("csvImport.removeButtonAria")}
					type="button"
					onClick={handleRemove}
					className="cursor-pointer rounded-md bg-red-600 w-24 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 ml-auto"
				>
					{t("csvImport.removeButton")}
				</button>
			</div>
		) : (
			<div
				{...getRootProps({
					className:
						"border border-dashed border-gray-400 rounded-sm w-[50%] h-80 aspect-square flex items-center justify-center cursor-pointer mt-2",
				})}
			>
				<input {...getInputProps({ id: "importCSV" })} />
				<div className="flex flex-col gap-2 items-center justify-center">
					<svg
						xmlns="http://www.w3.org/2000/svg"
						className="h-10 w-10 text-gray-400"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={2}
							d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
						/>
					</svg>
					<p className=" text-gray-400 font-semibold">Click to upload or drag and drop</p>
					<p className="text-gray-400 text-sm font-semibold">CSV format, up to 5MB</p>
				</div>
			</div>
		);
	return (
		<div>
			<label htmlFor="importCSV" className="mt-1 mb-4 text-sm text-slate-600">
				{t("csvImport.importCSVLabel")}
			</label>
			{error && <p className="text-red-600 font-semibold mb-2">{error}</p>}

			{status === "uploading" ? (
				<div className="w-full">
					<div className="h-3 bg-gray-200 rounded-full overflow-hidden">
						<div
							className="h-full bg-(--admin-color-primary-strong) animate-pulse w-1/2"
							style={{
								animation: "growProgress 3s ease-out forwards",
							}}
						></div>
					</div>
					<p className="text-center text-slate-700 mt-2 font-semibold">Uploading...</p>
				</div>
			) : (
				uploadContent
			)}
		</div>
	);
};
